import { createSelector } from '@reduxjs/toolkit';
import { EDIT_PERMISSIONS_OF_PROJECT } from 'scenes/Permissions/constants/permissions';
import { returnDefaultPermissionsByEmploymentType } from 'scenes/Permissions/utils/returnDefaultPermissionsByEmploymentType';
import {
  selectProjects,
  selectProjectsArray,
  selectProjectsFolder,
  selectSelectedProject,
  selectSelectedProjectId,
} from 'store/projects/selectors/projectsSelectors';
import {
  selectIsUserProjectResourcesLoaded,
  selectLoggedInUserResourcesArray,
  selectSelectedProjectCurrentUserResources,
} from 'store/resources/selectors/resources.selectors';
import {
  selectLoggedInUser,
  selectUserActiveEmployerBusinessId,
} from 'store/user/selectors.js/user.selectors';

import { uniqBy } from 'lodash-es';
import { BUSINESS_EXPLICIT_PERMISSIONS } from 'scenes/Permissions/constants/business-explicit-permissions';
import { PROJECT_EXPLICIT_PERMISSIONS } from 'scenes/Permissions/constants/project-explicit-permissions';
import { returnTogglableDefaultBusinessPermissions } from 'scenes/Permissions/utils/returnTogglableDefaultBusinessPermissions.util';
import { selectUserProfileEmployerBusinessIds } from 'store/userProfile/selectors/user-profile.selectors';
import {
  selectUserAdminBusinessIds,
  selectUserAdminPermissions,
} from './base-project-permissions.selectors';

const selectProjectPermissionsState = (state) => state.projectPermissions;

export const selectUserProjectPermissions = createSelector(
  [selectProjectPermissionsState],
  (state) => state.userProjectPermissionsByProjectId
);

export const selectUserBusinessPermissions = createSelector(
  [selectProjectPermissionsState],
  (state) => state.userBusinessPermissions
);

export const selectIsBusinessAdminOrOwnerOfSelectedProject = createSelector(
  [selectUserAdminPermissions, selectLoggedInUser, selectSelectedProject],
  (adminPermissions, loggedInUser, project) => {
    if (!project.business_id) {
      // in case of personal mylixani project, check if project created by current user
      return project.mirrorUser === loggedInUser.id;
    } else {
      // in business projects, check for admin status
      return adminPermissions[project.business_id] ? true : false;
    }
  }
);

export const selectResourceDefaultPermissions = createSelector(
  [selectSelectedProjectCurrentUserResources],
  (selectedProjectUserResources) => {
    const resourceDefaultPermissions = returnDefaultPermissionsByEmploymentType(
      selectedProjectUserResources
    );
    return resourceDefaultPermissions;
  }
);

export const selectIsCurrentUserEmployeeInProject = createSelector(
  [selectSelectedProjectCurrentUserResources],
  (currentUserResources) => {
    const hasEmployeeResource = currentUserResources.filter(
      (resource) =>
        !resource.employmentType || resource.employmentType === 'EMPLOYEE'
    )[0];

    return hasEmployeeResource ? true : false;
  }
);

export const getNearestUserProjectPermissions = (
  projects,
  userPermissions,
  selectedProjectId
) => {
  //If permissions are not set in current project, they are inherited from nearest parent project. Parent projects are looped until permissions are found.
  const selectedProjectPermissions = userPermissions[selectedProjectId];

  const selectedProject = projects[selectedProjectId];

  const getParentProjectPermissions = (projectId) => {
    const currentProject = projects[projectId];

    const parentProjectPermissions =
      currentProject && currentProject.parent
        ? userPermissions[currentProject.parent]
        : {};

    return parentProjectPermissions
      ? parentProjectPermissions
      : // if not found, call itself again, this time with the parent of current project in loop
        getParentProjectPermissions(currentProject.parent);
  };

  if (selectedProjectPermissions) {
    return selectedProjectPermissions;
  } else if (selectedProject && selectedProject.parent) {
    return getParentProjectPermissions(selectedProjectId);
  } else {
    return {};
  }
};

export const selectSelectedFolderUserPermissions = createSelector(
  [
    selectProjectsFolder,
    selectUserProjectPermissions,
    selectProjects,
    selectResourceDefaultPermissions,
  ],
  (
    selectedFolder,
    userProjectPermissions,
    projects,
    resourceDefaultPermissions
  ) => {
    const definedUserPermissions = getNearestUserProjectPermissions(
      projects,
      userProjectPermissions,
      selectedFolder.projectId
    );

    const resourcePermissionsWithDefinedPermissions = {
      ...resourceDefaultPermissions,
      ...definedUserPermissions,
    };

    return resourcePermissionsWithDefinedPermissions;
  }
);
export const selectSelectedProjectUserPermissions = createSelector(
  [
    selectSelectedProjectId,
    selectUserProjectPermissions,
    selectProjects,
    selectResourceDefaultPermissions,
  ],
  (
    selectedProjectId,
    userProjectPermissions,
    projects,
    resourceDefaultPermissions
  ) => {
    const definedUserPermissions = getNearestUserProjectPermissions(
      projects,
      userProjectPermissions,
      selectedProjectId
    );

    const effectivePermissions = {
      ...resourceDefaultPermissions,
      ...definedUserPermissions,
    };

    return effectivePermissions;
  }
);

export const selectCurrentUserBusinessPermissions = createSelector(
  [selectUserBusinessPermissions, selectProjects],
  (userBusinessPermissions) => {
    const defaultBusinessPermissions =
      returnTogglableDefaultBusinessPermissions();

    const effectivePermissions = {
      ...defaultBusinessPermissions,
      ...userBusinessPermissions,
    };

    return effectivePermissions;
  }
);

export const selectCanAccessPermissionsManagement = createSelector(
  [
    selectSelectedProjectUserPermissions,
    selectIsBusinessAdminOrOwnerOfSelectedProject,
  ],
  (userPermissions, isOwner) =>
    isOwner || userPermissions[EDIT_PERMISSIONS_OF_PROJECT] === true
);

export const selectSelectedProjectUserPermissionsArray = createSelector(
  [selectSelectedProjectUserPermissions],
  (permissions) => (permissions ? Object.keys(permissions) : [])
);

export const selectIsPersonalPermissionsLoaded = createSelector(
  [selectProjectPermissionsState],
  (state) => state.isPersonalPermissionsLoaded
);

export const selectIsPermissionsDataLoaded = createSelector(
  [selectIsPersonalPermissionsLoaded, selectIsUserProjectResourcesLoaded],
  (isPermissionsLoaded, isResourcesLoaded) =>
    isPermissionsLoaded && isResourcesLoaded
);

export const selectIsAdminInSelectedProject = createSelector(
  [selectSelectedProject, selectUserAdminPermissions],
  (selectedProject, userAdminPermissions) =>
    userAdminPermissions[selectedProject.business_id] ? true : false
);

// Multiple use permission selector, in case of not owner, the permission's true value is checked from user permissions.
export const selectIsRequiredPermission = createSelector(
  [
    selectIsBusinessAdminOrOwnerOfSelectedProject,
    selectSelectedProjectUserPermissions,
    (_, args) => args,
  ],
  (isOwner, permissions, args) => {
    if (PROJECT_EXPLICIT_PERMISSIONS.includes(args)) {
      return permissions[args] === true;
    }
    return isOwner || permissions[args] === true;
  }
);

export const selectIsRequiredBusinessPermissionByBusinessId = createSelector(
  [
    selectUserAdminBusinessIds,
    selectCurrentUserBusinessPermissions,
    (_, args) => args,
  ],
  (userAdminBusinessIds, businessPermissions, permissionArgs) => {
    const { businessId, permission } = permissionArgs;

    const thisBusinessPermissions = businessPermissions[businessId];

    if (BUSINESS_EXPLICIT_PERMISSIONS.includes(permission)) {
      return (
        thisBusinessPermissions && thisBusinessPermissions[permission] === true
      );
    }

    return userAdminBusinessIds.includes(businessId) ||
      (thisBusinessPermissions && thisBusinessPermissions[permission])
      ? true
      : false;
  }
);

export const selectIsRequiredBusinessPermissionFromCurrentActiveEmployerBusiness =
  createSelector(
    [
      selectUserAdminBusinessIds,
      selectCurrentUserBusinessPermissions,
      selectUserActiveEmployerBusinessId,
      (_, args) => args,
    ],
    (
      userAdminBusinessIds,
      businessPermissions,
      activeEmployerBusinessId,
      permission
    ) => {
      const thisBusinessPermissions =
        businessPermissions[activeEmployerBusinessId];

      return userAdminBusinessIds.includes(activeEmployerBusinessId) ||
        (thisBusinessPermissions && thisBusinessPermissions[permission])
        ? true
        : false;
    }
  );

export const selectEmployersWithPermissionToAdjustUserEmployerSettings =
  createSelector(
    [selectProjectsArray, selectUserProfileEmployerBusinessIds],
    (projects, adminBusinessIds) => {
      const userAdminEmployerProjects = projects.filter(
        (project) =>
          project.businessTypeEnum === 'EMPLOYER' &&
          adminBusinessIds.includes(project.business_id)
      );

      const uniqueBusinessIdEmployers = uniqBy(
        userAdminEmployerProjects,
        'business_id'
      );

      return uniqueBusinessIdEmployers;
    }
  );

export const selectIsRequiredPermissionForProject = createSelector(
  [
    selectUserAdminPermissions,
    selectUserProjectPermissions,
    selectProjects,
    selectLoggedInUserResourcesArray,
    (_, args) => args,
  ],
  (
    adminPermissions,
    userProjectPermissions,
    projects,
    loggedInUserResources,
    args
  ) => {
    const { projectId, permission } = args;
    const project = projects[projectId];

    if (!project) return false;

    const isAdmin = adminPermissions[project.business_id] ? true : false;
    if (isAdmin) return true;

    const definedUserPermissions = getNearestUserProjectPermissions(
      projects,
      userProjectPermissions,
      projectId
    );

    const projectResources = loggedInUserResources
      ? loggedInUserResources.filter(
          (resource) => resource.project === projectId
        )
      : [];
    const resourceDefaultPermissions =
      returnDefaultPermissionsByEmploymentType(projectResources);

    const effectivePermissions = {
      ...resourceDefaultPermissions,
      ...definedUserPermissions,
    };

    if (PROJECT_EXPLICIT_PERMISSIONS.includes(permission)) {
      return effectivePermissions[permission] === true;
    }

    return isAdmin || effectivePermissions[permission] === true;
  }
);
